"use client";

import { createContext, useContext, useState, useEffect, type ReactNode } from "react";
import useSWR from "swr";
import { fetcher } from "@/lib/api";
import type { ApiResponse, PortfolioSummary } from "@/types";

interface PortfolioContextType {
  portfolios: PortfolioSummary[];
  selectedPortfolio: string;
  setSelectedPortfolio: (name: string) => void;
  currency: "KRW" | "USD";
}

const PortfolioContext = createContext<PortfolioContextType>({
  portfolios: [],
  selectedPortfolio: "",
  setSelectedPortfolio: () => {},
  currency: "KRW",
});

const STORAGE_KEY = "ezsplit-selected-portfolio";

export function PortfolioProvider({ children }: { children: ReactNode }) {
  const [selected, setSelected] = useState<string | null>(null);

  const { data, error } = useSWR<ApiResponse<PortfolioSummary[]>>(
    "/api/portfolio/list",
    fetcher,
    {
      refreshInterval: 60000,
      shouldRetryOnError: true,
      errorRetryInterval: 10000,
      errorRetryCount: 3,
    }
  );

  // 로그인 사용자면 'userid-' 로 시작하는 포트폴리오만 노출 (예: song → song-xxx).
  // 비로그인(신뢰접근)은 전체.
  const { data: meData } = useSWR<{ user: string | null }>("/api/auth/me", fetcher, {
    revalidateOnFocus: false,
    refreshInterval: 0,
  });
  const authUser = meData?.user ?? null;
  const allPortfolios = data?.data ?? [];
  // user id prefix 룰은 대소문자 무시 (예: Song / song 모두 song-* 매칭)
  const authUserLc = authUser?.toLowerCase() ?? null;
  const portfolios = authUserLc
    ? allPortfolios.filter((p) => p.name.toLowerCase().startsWith(`${authUserLc}-`))
    : allPortfolios;

  // 포트폴리오 목록 로드 후 자동 선택 (이 탭의 최초 1회만)
  // localStorage 는 같은 브라우저의 모든 탭/창이 공유하므로, 목록 갱신(포커스/주기적
  // revalidate)마다 재적용하면 다른 탭의 선택이 현재 탭을 덮어쓰는 문제가 생김.
  // → selected 가 정해진 뒤에는 재적용하지 않아 각 탭이 자기 선택을 유지한다.
  useEffect(() => {
    if (portfolios.length === 0) return;
    if (selected) return;  // 이미 선택됨 → 재적용 금지 (탭 간 localStorage 공유 간섭 차단)

    // localStorage 복원 시도
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && portfolios.some(p => p.name === saved)) {
      setSelected(saved);
      return;
    }

    // 없으면 첫 번째 자동 선택
    setSelected(portfolios[0].name);
    localStorage.setItem(STORAGE_KEY, portfolios[0].name);
  }, [portfolios, selected]);

  const setSelectedPortfolio = (name: string) => {
    setSelected(name);
    localStorage.setItem(STORAGE_KEY, name);
  };

  // 첫 로딩 시(데이터 도착 전)만 스피너 표시.
  // selected 가 한번 정해지면 이후 SWR 재검증으로 data 가 잠시 비어도
  // children 을 언마운트하지 않음 (false alarm 으로 전체 페이지 reload 처럼 느껴지는 현상 방지).
  const isFirstLoad = !data && !error;
  if (!selected || isFirstLoad) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-[#f5f5f7]">
        <div className="w-5 h-5 border-2 border-black/20 border-t-black/60 rounded-full animate-spin" />
        {error && (
          <p className="mt-3 text-sm text-black/55 tracking-[-0.224px]">API 서버 연결 중...</p>
        )}
      </div>
    );
  }

  // 여기부터 selected는 반드시 유효한 string
  return (
    <PortfolioContext.Provider
      value={{
        portfolios,
        selectedPortfolio: selected,
        setSelectedPortfolio,
        currency: portfolios.find(p => p.name === selected)?.market === "overseas" ? "USD" : "KRW",
      }}
    >
      {children}
    </PortfolioContext.Provider>
  );
}

export function usePortfolioContext() {
  return useContext(PortfolioContext);
}
